import { useCallback, useEffect, useRef, useState } from "react";
import { getRetrabalhos, adicionarNaSprint } from "../../api/retrabalho.api";
import { getSetupSprint, getSetupTickets } from "../../api/setup.api";
import { getSprintDevs } from "../../api/sprint.api";
import TicketDrawer from "../../components/TicketDrawer";

const REFRESH_SEC = 60;

const MOTIVOS = {
  MR01: { label: "Especificação incorreta (Autor)",           tipo: "autor",        color: "#fb923c" },
  MR02: { label: "Implementação incompleta (Desenvolvedor)",  tipo: "desenvolvedor", color: "#f87171" },
  MR03: { label: "Implementação errada (Desenvolvedor)",      tipo: "desenvolvedor", color: "#fca5a5" },
  MR04: { label: "Especificação incorreta (NP)",              tipo: "np",           color: "#a78bfa" },
  MR05: { label: "Falha nos testes (NP)",                     tipo: "np",           color: "#818cf8" },
};

const TIPO_COLOR = { desenvolvedor: "#f87171", np: "#a78bfa", autor: "#fb923c" };
const TIPO_LABEL = { desenvolvedor: "Desenvolvedor", np: "NP / Produto", autor: "Autor" };

const DEV_AVATAR_MAP = [
  { match: "guilherme", bg: "#712B13", color: "#F5C4B3" },
  { match: "paulo",     bg: "#0C447C", color: "#B5D4F4" },
  { match: "marcelo",   bg: "#085041", color: "#9FE1CB" },
  { match: "rudi",      bg: "#3C3489", color: "#CECBF6" },
  { match: "nelson",    bg: "#1C5040", color: "#A0DFC4" },
];

function devAvatarCores(nome) {
  if (!nome) return { bg: "#2C2C2A", color: "#B4B2A9" };
  const low = nome.toLowerCase();
  for (const m of DEV_AVATAR_MAP) if (low.includes(m.match)) return m;
  return { bg: "#2C2C2A", color: "#B4B2A9" };
}

function devAvatarInitial(nome) {
  if (!nome) return "?";
  const parts = nome.trim().split(/\s+/);
  return parts.length >= 2
    ? (parts[0][0] + parts[1][0]).toUpperCase()
    : parts[0][0].toUpperCase();
}

function parseHoras(str) {
  if (!str) return 0;
  const parts = String(str).split(":");
  if (parts.length !== 2) return parseFloat(str) || 0;
  return (parseInt(parts[0], 10) || 0) + (parseInt(parts[1], 10) || 0) / 60;
}

function formatHoras(h) {
  if (!h || h === 0) return "—";
  const hrs = Math.floor(h);
  const min  = Math.round((h - hrs) * 60);
  return `${hrs}:${String(min).padStart(2, "0")}`;
}

function extractCodigo(motivo) {
  if (!motivo) return null;
  const m = String(motivo).match(/\[?(MR\d+)\]?/i);
  return m ? m[1].toUpperCase() : null;
}

function computeStats(tickets) {
  const devMap     = {};
  const motivoCount = {};
  let tempoTotal   = 0;

  for (const t of tickets) {
    const dev = t.desenvolvedor || "(Sem desenvolvedor)";
    if (!devMap[dev]) devMap[dev] = { nome: dev, total: 0, tempo: 0 };
    devMap[dev].total++;
    devMap[dev].tempo += parseHoras(t.tempo_gasto);

    const cod = extractCodigo(t.retrabalho_motivo);
    if (cod) motivoCount[cod] = (motivoCount[cod] || 0) + 1;

    tempoTotal += parseHoras(t.tempo_gasto);
  }

  const porDev = Object.values(devMap).sort((a, b) => b.total - a.total);
  const porMotivo = Object.entries(motivoCount)
    .map(([codigo, total]) => ({ codigo, total }))
    .sort((a, b) => b.total - a.total);

  const devResp   = porMotivo.filter(m => MOTIVOS[m.codigo]?.tipo === "desenvolvedor").reduce((s, m) => s + m.total, 0);
  const npResp    = porMotivo.filter(m => MOTIVOS[m.codigo]?.tipo === "np").reduce((s, m) => s + m.total, 0);
  const autorResp = porMotivo.filter(m => MOTIVOS[m.codigo]?.tipo === "autor").reduce((s, m) => s + m.total, 0);
  const semMotivo = tickets.length - devResp - npResp - autorResp;

  return { porDev, porMotivo, tempoTotal, devResp, npResp, autorResp, semMotivo };
}

/* ══════════════════════════════════════
   Page
══════════════════════════════════════ */
export default function Retrabalhos() {
  const [tickets,    setTickets]    = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState(false);
  const [filtroDev,  setFiltroDev]  = useState("todos");
  const [filtroMot,  setFiltroMot]  = useState("todos");
  const [sortBy,     setSortBy]     = useState("dev");
  const [sortDir,    setSortDir]    = useState("asc");
  const [countdown,  setCountdown]  = useState(REFRESH_SEC);
  const [drawer,     setDrawer]     = useState({ open: false, fetchId: 0 });
  const [sprintModal, setSprintModal] = useState(null);

  const loadRef = useRef(null);

  const openDrawer  = useCallback((cfg) =>
    setDrawer(prev => ({ ...cfg, open: true, fetchId: prev.fetchId + 1 })), []);
  const closeDrawer = useCallback(() =>
    setDrawer(prev => ({ ...prev, open: false })), []);

  async function load() {
    setError(false);
    try {
      const data = await getRetrabalhos();
      setTickets(data);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }

  loadRef.current = load;

  useEffect(() => {
    loadRef.current();
    const id = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) { loadRef.current(); return REFRESH_SEC; }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(id);
  }, []);

  /* Clique na linha → abre modal de sprint
     Clique no #número → abre Redmine (stopPropagation evita abrir modal junto) */

  /* ── Clique no desenvolvedor ── */
  function handleDevClick(devNome) {
    const devTickets = tickets.filter(t => t.desenvolvedor === devNome);
    openDrawer({
      title:    devNome,
      subtitle: `${devTickets.length} retrabalho${devTickets.length !== 1 ? "s" : ""}`,
      color:    "#f87171",
      fetchTickets: async () => devTickets.map(t => ({
        id:           t.id,
        subject:      t.titulo,
        titulo:       t.titulo,
        status:       t.status || "Retrabalhar",
        desenvolvedor: t.desenvolvedor,
        programado:   null,
        programacao_tipo: null,
      })),
    });
  }

  /* ── Stats / filtros / ordenação ── */
  const stats  = computeStats(tickets);
  const total  = tickets.length;
  const devs   = ["todos", ...stats.porDev.map(d => d.nome)];
  const motivos = ["todos", ...Object.keys(MOTIVOS)];

  const filtered = tickets.filter(t => {
    const devOk = filtroDev === "todos" || t.desenvolvedor === filtroDev;
    const motOk = filtroMot === "todos" || extractCodigo(t.retrabalho_motivo) === filtroMot;
    return devOk && motOk;
  });

  function toggleSort(col) {
    if (sortBy === col) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortBy(col); setSortDir("asc"); }
  }

  const sorted = [...filtered].sort((a, b) => {
    let va, vb;
    if      (sortBy === "dev")    { va = a.desenvolvedor || ""; vb = b.desenvolvedor || ""; }
    else if (sortBy === "motivo") { va = extractCodigo(a.retrabalho_motivo) || ""; vb = extractCodigo(b.retrabalho_motivo) || ""; }
    else if (sortBy === "tempo")  { va = parseHoras(a.tempo_gasto); vb = parseHoras(b.tempo_gasto); }
    else if (sortBy === "id")     { va = a.id || 0; vb = b.id || 0; }
    else                          { va = a.desenvolvedor || ""; vb = b.desenvolvedor || ""; }
    const cmp = typeof va === "string" ? va.localeCompare(vb, "pt-BR") : va - vb;
    return sortDir === "asc" ? cmp : -cmp;
  });

  const SortIcon = ({ col }) =>
    sortBy !== col
      ? <span style={{ color: "#334155", marginLeft: 4 }}>⇅</span>
      : <span style={{ color: "#60a5fa", marginLeft: 4 }}>{sortDir === "asc" ? "↑" : "↓"}</span>;

  if (loading) return (
    <div style={page}>
      <div style={{ color: "#475569", fontSize: 13, padding: 40, textAlign: "center" }}>Carregando retrabalhos…</div>
    </div>
  );

  if (error) return (
    <div style={page}>
      <div style={{ color: "#f87171", textAlign: "center", marginTop: 60, fontSize: 14 }}>
        Erro ao carregar dados.{" "}
        <button onClick={load} style={{ color: "#60a5fa", background: "none", border: "none", cursor: "pointer", fontSize: 14 }}>
          Tentar novamente
        </button>
      </div>
    </div>
  );

  return (
    <div style={page}>

      {/* ── Header ── */}
      <header style={header}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <div>
            <h1 style={h1}>Retrabalhos</h1>
            <div style={{ fontSize: 12, color: "#475569", marginTop: 2 }}>
              {total} ticket{total !== 1 ? "s" : ""} rastreados
            </div>
          </div>
          {stats.devResp  > 0 && <span style={badgeDev}>{stats.devResp} desenvolvedor</span>}
          {stats.npResp   > 0 && <span style={badgeNp}>{stats.npResp} NP / Produto</span>}
          {stats.autorResp > 0 && <span style={badgeAutor}>{stats.autorResp} autor</span>}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <span style={{ fontSize: 11, color: "#475569" }}>Atualiza em {countdown}s</span>
          <button onClick={() => { loadRef.current(); setCountdown(REFRESH_SEC); }} title="Atualizar agora" style={btnRefresh}>↻</button>
        </div>
      </header>

      {/* ── KPI Cards ── */}
      <section style={kpiGrid}>
        <KpiCard label="Total de Retrabalhos" value={total}                          color="#e2e8f0" />
        <KpiCard label="Tempo Total Gasto"    value={formatHoras(stats.tempoTotal)}  color="#60a5fa" />
        <KpiCard
          label="Responsabilidade Desenvolvedor"
          value={`${stats.devResp} (${total ? Math.round(stats.devResp / total * 100) : 0}%)`}
          color="#f87171"
        />
        <KpiCard
          label="Responsabilidade NP / Produto"
          value={`${stats.npResp} (${total ? Math.round(stats.npResp / total * 100) : 0}%)`}
          color="#a78bfa"
        />
        <KpiCard
          label="Responsabilidade Autor"
          value={`${stats.autorResp} (${total ? Math.round(stats.autorResp / total * 100) : 0}%)`}
          color="#fb923c"
        />
        {stats.semMotivo > 0 && (
          <KpiCard
            label="Sem Motivo Definido"
            value={`${stats.semMotivo} (${total ? Math.round(stats.semMotivo / total * 100) : 0}%)`}
            color="#475569"
          />
        )}
      </section>

      {/* ── Grid: Por Desenvolvedor + Por Motivo ── */}
      <section style={mainGrid}>

        {/* Por Desenvolvedor */}
        <div style={glass}>
          <div style={sectionLabel}>Por Desenvolvedor</div>
          <div style={{ fontSize: 11, color: "#334155", marginBottom: 14, marginTop: -10 }}>
            Clique para ver os tickets do desenvolvedor
          </div>
          {stats.porDev.length === 0
            ? <div style={emptyMsg}>Sem dados.</div>
            : stats.porDev.map(d => {
                const pct = total ? Math.round(d.total / total * 100) : 0;
                const av  = devAvatarCores(d.nome);
                return (
                  <div
                    key={d.nome}
                    style={{ ...devRow, cursor: "pointer" }}
                    onClick={() => handleDevClick(d.nome)}
                    onMouseEnter={e => { e.currentTarget.style.background = "rgba(248,113,113,0.06)"; e.currentTarget.style.borderRadius = "8px"; }}
                    onMouseLeave={e => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.borderRadius = "0"; }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: 10, flex: 1, minWidth: 0 }}>
                      <div style={{ width: 30, height: 30, borderRadius: "50%", background: av.bg, color: av.color, fontSize: 11, fontWeight: 600, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                        {devAvatarInitial(d.nome)}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 5 }}>
                          <span style={{ fontSize: 13, color: "#e2e8f0", fontWeight: 500 }}>{d.nome}</span>
                          <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                            <span style={{ fontSize: 11, color: "#475569" }}>{formatHoras(d.tempo)}</span>
                            <span style={{ fontSize: 14, color: "#f87171", fontWeight: 700 }}>{d.total}</span>
                          </div>
                        </div>
                        <div style={{ height: 4, background: "rgba(255,255,255,0.07)", borderRadius: 2, overflow: "hidden" }}>
                          <div style={{ height: "100%", width: `${pct}%`, background: "#f87171", borderRadius: 2, transition: "width 0.4s" }} />
                        </div>
                        <div style={{ fontSize: 10, color: "#334155", marginTop: 3 }}>{pct}% do total</div>
                      </div>
                    </div>
                  </div>
                );
              })
          }
        </div>

        {/* Por Motivo */}
        <div style={glass}>
          <div style={sectionLabel}>Por Motivo</div>
          {stats.porMotivo.length === 0 && stats.semMotivo === 0
            ? <div style={emptyMsg}>Sem dados.</div>
            : (
              <>
                {stats.porMotivo.map(m => {
                  const info = MOTIVOS[m.codigo] || { label: m.codigo, tipo: "desconhecido", color: "#94a3b8" };
                  const pct  = total ? Math.round(m.total / total * 100) : 0;
                  return (
                    <div key={m.codigo} style={motivoRow}>
                      <div style={{ display: "flex", alignItems: "flex-start", gap: 8, marginBottom: 6 }}>
                        <span style={{ fontSize: 11, fontWeight: 800, color: info.color, background: `${info.color}1a`, padding: "2px 8px", borderRadius: 4, letterSpacing: "0.05em", flexShrink: 0, marginTop: 1 }}>
                          {m.codigo}
                        </span>
                        <span style={{ fontSize: 12, color: "#94a3b8", flex: 1, lineHeight: 1.4 }}>{info.label}</span>
                        <span style={{ fontSize: 14, fontWeight: 700, color: info.color, flexShrink: 0 }}>{m.total}</span>
                      </div>
                      <div style={{ height: 4, background: "rgba(255,255,255,0.07)", borderRadius: 2, overflow: "hidden", marginBottom: 4 }}>
                        <div style={{ height: "100%", width: `${pct}%`, background: info.color, borderRadius: 2, transition: "width 0.4s" }} />
                      </div>
                      <div style={{ display: "flex", justifyContent: "space-between" }}>
                        <span style={{ fontSize: 10, color: "#334155" }}>{pct}% do total</span>
                        <span style={{ fontSize: 10, fontWeight: 600, color: TIPO_COLOR[info.tipo] || "#94a3b8" }}>
                          {TIPO_LABEL[info.tipo] || info.tipo}
                        </span>
                      </div>
                    </div>
                  );
                })}
                {stats.semMotivo > 0 && (
                  <div style={motivoRow}>
                    <div style={{ display: "flex", alignItems: "flex-start", gap: 8, marginBottom: 6 }}>
                      <span style={{ fontSize: 11, fontWeight: 800, color: "#475569", background: "rgba(71,85,105,0.15)", padding: "2px 8px", borderRadius: 4, letterSpacing: "0.05em", flexShrink: 0, marginTop: 1 }}>
                        —
                      </span>
                      <span style={{ fontSize: 12, color: "#64748b", flex: 1, lineHeight: 1.4 }}>Sem motivo definido</span>
                      <span style={{ fontSize: 14, fontWeight: 700, color: "#475569", flexShrink: 0 }}>{stats.semMotivo}</span>
                    </div>
                    <div style={{ height: 4, background: "rgba(255,255,255,0.07)", borderRadius: 2, overflow: "hidden", marginBottom: 4 }}>
                      <div style={{ height: "100%", width: `${total ? Math.round(stats.semMotivo / total * 100) : 0}%`, background: "#475569", borderRadius: 2, transition: "width 0.4s" }} />
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between" }}>
                      <span style={{ fontSize: 10, color: "#334155" }}>{total ? Math.round(stats.semMotivo / total * 100) : 0}% do total</span>
                      <span style={{ fontSize: 10, color: "#334155" }}>campo não preenchido</span>
                    </div>
                  </div>
                )}
              </>
            )
          }
        </div>

      </section>

      {/* ── Tabela completa ── */}
      <section style={{ marginBottom: 40 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8, flexWrap: "wrap" }}>
          <div style={sectionLabel}>Todos os Retrabalhos</div>
          <span style={{ fontSize: 11, color: "#334155" }}>
            {filtered.length !== total ? `${filtered.length} de ${total}` : `${total} tickets`}
          </span>
          <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
            <select value={filtroDev} onChange={e => setFiltroDev(e.target.value)} style={selectStyle}>
              {devs.map(d => (
                <option key={d} value={d}>{d === "todos" ? "Todos os desenvolvedores" : d}</option>
              ))}
            </select>
            <select value={filtroMot} onChange={e => setFiltroMot(e.target.value)} style={selectStyle}>
              {motivos.map(m => (
                <option key={m} value={m}>{m === "todos" ? "Todos os motivos" : `${m} — ${MOTIVOS[m]?.label || m}`}</option>
              ))}
            </select>
          </div>
        </div>
        <div style={{ fontSize: 11, color: "#334155", marginBottom: 10 }}>
          Clique na linha para adicionar na sprint · Clique no <strong style={{ color: "#60a5fa" }}>#número</strong> para abrir no Redmine
        </div>

        {filtered.length === 0
          ? <div style={emptyMsg}>Nenhum ticket encontrado com os filtros aplicados.</div>
          : (
            <div style={tableWrap}>
              <table style={table}>
                <thead>
                  <tr>
                    <th style={{ ...th, cursor: "pointer" }} onClick={() => toggleSort("id")}>
                      Ticket <SortIcon col="id" />
                    </th>
                    <th style={{ ...th, cursor: "pointer" }} onClick={() => toggleSort("dev")}>
                      Desenvolvedor <SortIcon col="dev" />
                    </th>
                    <th style={{ ...th, cursor: "pointer" }} onClick={() => toggleSort("motivo")}>
                      Motivo <SortIcon col="motivo" />
                    </th>
                    <th style={th}>Tipo</th>
                    <th style={th}>Grupo de Entrega</th>
                    <th style={{ ...th, cursor: "pointer" }} onClick={() => toggleSort("tempo")}>
                      Tempo Gasto <SortIcon col="tempo" />
                    </th>
                    <th style={th}>Título</th>
                  </tr>
                </thead>
                <tbody>
                  {sorted.map(t => {
                    const cod  = extractCodigo(t.retrabalho_motivo);
                    const info = cod ? (MOTIVOS[cod] || { color: "#94a3b8", label: t.retrabalho_motivo || "—", tipo: null }) : null;
                    const av   = devAvatarCores(t.desenvolvedor);
                    return (
                      <tr
                        key={t.id}
                        style={{ ...trRow, cursor: "pointer" }}
                        onClick={() => setSprintModal(t)}
                        onMouseEnter={e => { e.currentTarget.style.background = "rgba(255,255,255,0.03)"; }}
                        onMouseLeave={e => { e.currentTarget.style.background = "transparent"; }}
                      >
                        <td style={td}>
                          <a
                            href={`https://redmine.lexsis.com.br/issues/${t.id}`}
                            target="_blank"
                            rel="noreferrer"
                            onClick={e => e.stopPropagation()}
                            title="Abrir no Redmine"
                            style={{ color: "#60a5fa", fontWeight: 600, textDecoration: "none" }}
                          >
                            #{t.id}
                          </a>
                        </td>
                        <td style={td}>
                          <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
                            <div style={{ width: 22, height: 22, borderRadius: "50%", background: av.bg, color: av.color, fontSize: 9, fontWeight: 600, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                              {devAvatarInitial(t.desenvolvedor)}
                            </div>
                            <span style={{ fontSize: 12, color: "#e2e8f0" }}>{t.desenvolvedor || "—"}</span>
                          </div>
                        </td>
                        <td style={td}>
                          {info ? (
                            <div>
                              <span style={{ fontSize: 11, fontWeight: 700, color: info.color, background: `${info.color}1a`, padding: "2px 7px", borderRadius: 4 }}>
                                {cod}
                              </span>
                              <div style={{ fontSize: 11, color: "#64748b", marginTop: 3, maxWidth: 200 }}>{info.label}</div>
                            </div>
                          ) : <span style={{ color: "#334155", fontSize: 12 }}>—</span>}
                        </td>
                        <td style={{ ...td, fontSize: 12, color: "#94a3b8" }}>{t.tipo || "—"}</td>
                        <td style={td}>
                          <span style={{ fontSize: 10, color: "#64748b", background: "rgba(71,85,105,0.18)", padding: "2px 7px", borderRadius: 4 }}>
                            {t.grupo_entrega || "—"}
                          </span>
                        </td>
                        <td style={{ ...td, fontSize: 12, color: "#60a5fa", fontWeight: 600, whiteSpace: "nowrap" }}>
                          {t.tempo_gasto || "—"}
                        </td>
                        <td style={{ ...td, maxWidth: 300 }}>
                          <span
                            style={{ fontSize: 12, color: "#e2e8f0", display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                            title={t.titulo}
                          >
                            {t.titulo || "—"}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )
        }
      </section>

      {/* ── Sprint Modal ── */}
      <SprintModal
        ticket={sprintModal}
        onClose={() => setSprintModal(null)}
        onSuccess={() => { load(); setSprintModal(null); }}
      />

      {/* ── Ticket Drawer (por desenvolvedor) ── */}
      <TicketDrawer
        open={drawer.open}
        onClose={closeDrawer}
        title={drawer.title}
        subtitle={drawer.subtitle}
        color={drawer.color}
        fetchTickets={drawer.fetchTickets}
        fetchId={drawer.fetchId}
      />

    </div>
  );
}

/* ══════════════════════════════════════
   SprintModal
══════════════════════════════════════ */
function SprintModal({ ticket, onClose, onSuccess }) {
  const [versao,           setVersao]           = useState("");
  const [grupoEntrega,     setGrupoEntrega]      = useState("");
  const [tipo,             setTipo]              = useState(1);
  const [desenvolvedor,    setDesenvolvedor]     = useState("");
  const [devOptions,       setDevOptions]        = useState([]);
  const [ehProgAtivo,      setEhProgAtivo]       = useState(false);
  const [checkingProg,     setCheckingProg]      = useState(true);
  const [saving,           setSaving]            = useState(false);
  const [saved,            setSaved]             = useState(false);
  const [savedJaProg,      setSavedJaProg]       = useState(false);
  const [err,              setErr]               = useState(false);

  useEffect(() => {
    if (!ticket) return;
    setSaved(false);
    setErr(false);
    setCheckingProg(true);
    setDesenvolvedor(ticket.desenvolvedor || "");

    /* Sprint atual → pré-preenche campos */
    getSetupSprint().then(d => {
      setVersao(d?.versao || "");
      setGrupoEntrega(d?.grupo_entrega || d?.sprint || "");
    }).catch(() => {});

    /* Desenvolvedores disponíveis */
    getSprintDevs().then(d => {
      const nomes = (d?.developers || []).map(dev => dev.nome).filter(Boolean);
      setDevOptions(nomes);
    }).catch(() => {});

    /* Verifica se o ticket já está na programação ativa da sprint */
    getSetupTickets().then(setupTickets => {
      const ids = (setupTickets || []).map(t => Number(t.id ?? t.ticket_id ?? 0));
      setEhProgAtivo(ids.includes(Number(ticket.id)));
    }).catch(() => {
      setEhProgAtivo(false);
    }).finally(() => setCheckingProg(false));
  }, [ticket]);

  async function handleSave() {
    setSaving(true);
    setErr(false);
    try {
      const res = await adicionarNaSprint(ticket.id, {
        versao,
        grupo_entrega:    grupoEntrega,
        programacao_tipo: tipo,
        desenvolvedor,
        eh_programado_ativo: ehProgAtivo,
      });
      setSavedJaProg(res?.ja_programado ?? ehProgAtivo);
      setSaved(true);
      setTimeout(() => onSuccess?.(), 1800);
    } catch {
      setErr(true);
      setSaving(false);
    }
  }

  if (!ticket) return null;

  const cod  = extractCodigo(ticket.retrabalho_motivo);
  const info = cod ? (MOTIVOS[cod] || { color: "#94a3b8", label: ticket.retrabalho_motivo || "—" }) : null;
  const av   = devAvatarCores(ticket.desenvolvedor);

  return (
    <div
      style={{
        position: "fixed", inset: 0,
        background: "rgba(0,0,0,0.72)",
        backdropFilter: "blur(4px)",
        WebkitBackdropFilter: "blur(4px)",
        zIndex: 300,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: "#0f1623",
          border: "1px solid rgba(96,165,250,0.14)",
          borderRadius: 16,
          width: "100%",
          maxWidth: 520,
          boxShadow: "0 24px 72px rgba(0,0,0,0.75)",
          overflow: "hidden",
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ padding: "20px 24px 16px", borderBottom: "1px solid rgba(255,255,255,0.06)", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, color: "#f1f5f9" }}>Adicionar na Sprint</div>
            <div style={{ fontSize: 12, color: "#475569", marginTop: 2 }}>Ticket #{ticket.id}</div>
          </div>
          <button
            onClick={onClose}
            style={{ width: 28, height: 28, borderRadius: 6, background: "rgba(255,255,255,0.06)", border: "0.5px solid rgba(255,255,255,0.1)", color: "rgba(255,255,255,0.5)", cursor: "pointer", fontSize: 12, display: "flex", alignItems: "center", justifyContent: "center" }}
          >✕</button>
        </div>

        {/* Ticket info */}
        <div style={{ padding: "14px 24px", borderBottom: "1px solid rgba(255,255,255,0.05)" }}>
          <div style={{ fontSize: 13, color: "#e2e8f0", lineHeight: 1.55, marginBottom: 10 }}>
            {ticket.titulo || `Ticket #${ticket.id}`}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
              <div style={{ width: 20, height: 20, borderRadius: "50%", background: av.bg, color: av.color, fontSize: 8, fontWeight: 600, display: "flex", alignItems: "center", justifyContent: "center" }}>
                {devAvatarInitial(ticket.desenvolvedor)}
              </div>
              <span style={{ fontSize: 12, color: "#64748b" }}>{ticket.desenvolvedor || "Sem desenvolvedor"}</span>
            </div>
            {info && (
              <span style={{ fontSize: 11, fontWeight: 700, color: info.color, background: `${info.color}1a`, padding: "2px 8px", borderRadius: 4 }}>
                {cod} — {info.label}
              </span>
            )}
          </div>
        </div>

        {/* Form */}
        <div style={{ padding: "20px 24px" }}>

          {/* Aviso de programação ativa */}
          {!checkingProg && (
            <div style={{
              display: "flex",
              gap: 10,
              alignItems: "flex-start",
              padding: "10px 14px",
              borderRadius: 8,
              marginBottom: 16,
              background: ehProgAtivo
                ? "rgba(96,165,250,0.08)"
                : "rgba(251,146,60,0.08)",
              border: ehProgAtivo
                ? "1px solid rgba(96,165,250,0.2)"
                : "1px solid rgba(251,146,60,0.2)",
            }}>
              <span style={{ fontSize: 16, flexShrink: 0, lineHeight: 1.2 }}>
                {ehProgAtivo ? "ℹ️" : "⚠️"}
              </span>
              <div>
                {ehProgAtivo ? (
                  <>
                    <div style={{ fontSize: 12, fontWeight: 600, color: "#60a5fa", marginBottom: 2 }}>
                      Ticket já consta na programação desta sprint
                    </div>
                    <div style={{ fontSize: 11, color: "#64748b", lineHeight: 1.5 }}>
                      O campo <strong style={{ color: "#94a3b8" }}>Programado</strong> será mantido como <strong style={{ color: "#4ade80" }}>Sim</strong>.
                      O campo <strong style={{ color: "#94a3b8" }}>Emergência</strong> <strong style={{ color: "#f87171" }}>não</strong> será alterado.
                    </div>
                  </>
                ) : (
                  <>
                    <div style={{ fontSize: 12, fontWeight: 600, color: "#fb923c", marginBottom: 2 }}>
                      Retrabalho fora da programação ativa
                    </div>
                    <div style={{ fontSize: 11, color: "#64748b", lineHeight: 1.5 }}>
                      Será marcado automaticamente: <strong style={{ color: "#fb923c" }}>Emergência = Sim</strong> e{" "}
                      <strong style={{ color: "#f87171" }}>Programado = Não</strong>.
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          {/* Desenvolvedor */}
          <div style={{ marginBottom: 14 }}>
            <label style={formLabel}>Programador</label>
            {devOptions.length > 0 ? (
              <select
                value={desenvolvedor}
                onChange={e => setDesenvolvedor(e.target.value)}
                style={{ ...formInput, cursor: "pointer" }}
              >
                <option value="">— Selecionar programador —</option>
                {devOptions.map(n => (
                  <option key={n} value={n}>{n}</option>
                ))}
              </select>
            ) : (
              <input
                value={desenvolvedor}
                onChange={e => setDesenvolvedor(e.target.value)}
                style={formInput}
                placeholder="Nome do programador"
              />
            )}
          </div>

          {/* Versão + Grupo */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 14 }}>
            <div>
              <label style={formLabel}>Versão</label>
              <input
                value={versao}
                onChange={e => setVersao(e.target.value)}
                style={formInput}
                placeholder="Ex: 901"
              />
            </div>
            <div>
              <label style={formLabel}>Grupo de Entrega</label>
              <input
                value={grupoEntrega}
                onChange={e => setGrupoEntrega(e.target.value)}
                style={formInput}
                placeholder="Ex: 260303"
              />
            </div>
          </div>

          {/* Tipo T1/T2 */}
          <div style={{ marginBottom: 22 }}>
            <label style={formLabel}>Tipo de Programação</label>
            <div style={{ display: "flex", gap: 8 }}>
              {[{ v: 1, l: "T1 — Principal" }, { v: 2, l: "T2 — Secundário" }].map(({ v, l }) => (
                <button
                  key={v}
                  onClick={() => setTipo(v)}
                  style={{
                    flex: 1, padding: "9px 12px", borderRadius: 8, cursor: "pointer",
                    fontSize: 12, fontWeight: 500, transition: "all 0.15s",
                    background: tipo === v ? "rgba(83,74,183,0.25)" : "rgba(255,255,255,0.04)",
                    border:     tipo === v ? "1px solid rgba(83,74,183,0.5)" : "1px solid rgba(255,255,255,0.1)",
                    color:      tipo === v ? "#AFA9EC" : "rgba(255,255,255,0.4)",
                  }}
                >
                  {l}
                </button>
              ))}
            </div>
          </div>

          {err && (
            <div style={{ fontSize: 12, color: "#f87171", marginBottom: 12, padding: "8px 12px", background: "rgba(248,113,113,0.08)", borderRadius: 6, border: "1px solid rgba(248,113,113,0.2)" }}>
              Erro ao adicionar na sprint. Verifique os dados e tente novamente.
            </div>
          )}
          {saved && (
            <div style={{ fontSize: 12, marginBottom: 12, padding: "10px 12px", background: "rgba(74,222,128,0.08)", borderRadius: 6, border: "1px solid rgba(74,222,128,0.2)" }}>
              <div style={{ color: "#4ade80", fontWeight: 600, marginBottom: 4 }}>Ticket adicionado com sucesso!</div>
              <div style={{ color: "#64748b", lineHeight: 1.5 }}>
                {savedJaProg
                  ? <>Programado mantido como <strong style={{ color: "#60a5fa" }}>Sim</strong> — Emergência não alterada.</>
                  : <>Marcado como <strong style={{ color: "#fb923c" }}>Emergência = Sim</strong> e <strong style={{ color: "#f87171" }}>Programado = Não</strong>.</>
                }
              </div>
            </div>
          )}

          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
            <button onClick={onClose} style={btnSecondary}>Cancelar</button>
            <button
              onClick={handleSave}
              disabled={saving || saved}
              style={{ ...btnPrimary, opacity: saving || saved ? 0.7 : 1, cursor: saving || saved ? "default" : "pointer" }}
            >
              {saving ? "Adicionando…" : saved ? "Adicionado!" : "Adicionar na Sprint"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── Sub-components ── */

function KpiCard({ label, value, color }) {
  const [hov, setHov] = useState(false);
  return (
    <div
      style={{
        ...glass,
        display: "flex", flexDirection: "column", gap: 4,
        borderLeft: `3px solid ${color}`,
        borderRadius: "0 10px 10px 0",
        background: hov ? "rgba(255,255,255,0.04)" : glass.background,
        transition: "background 0.2s",
      }}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
    >
      <div style={{ fontSize: 10, letterSpacing: "0.08em", color: "rgba(255,255,255,0.35)", textTransform: "uppercase", marginBottom: 4 }}>
        {label}
      </div>
      <div style={{ fontSize: 26, fontWeight: 800, color, lineHeight: 1 }}>{value}</div>
    </div>
  );
}

/* ── Styles ── */

const page = {
  padding: "28px 32px",
  minHeight: "100vh",
  color: "#f1f5f9",
  fontFamily: "'Inter', sans-serif",
};

const header = {
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "space-between",
  marginBottom: 24,
  gap: 12,
};

const h1 = { fontSize: 22, fontWeight: 800, color: "#f1f5f9", margin: 0, letterSpacing: "-0.01em" };

const badgeDev   = { fontSize: 11, fontWeight: 600, color: "#f87171", background: "rgba(248,113,113,0.1)",  border: "1px solid rgba(248,113,113,0.2)",  borderRadius: 6, padding: "2px 10px" };
const badgeNp    = { fontSize: 11, fontWeight: 600, color: "#a78bfa", background: "rgba(167,139,250,0.1)", border: "1px solid rgba(167,139,250,0.2)", borderRadius: 6, padding: "2px 10px" };
const badgeAutor = { fontSize: 11, fontWeight: 600, color: "#fb923c", background: "rgba(251,146,60,0.1)",  border: "1px solid rgba(251,146,60,0.2)",  borderRadius: 6, padding: "2px 10px" };

const btnRefresh = {
  background: "rgba(96,165,250,0.08)", border: "1px solid rgba(96,165,250,0.15)",
  color: "#60a5fa", borderRadius: 8, padding: "6px 14px", cursor: "pointer", fontSize: 16,
};

const kpiGrid = {
  display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(190px, 1fr))", gap: 12, marginBottom: 24,
};

const glass = {
  background: "rgba(11,22,42,0.72)",
  backdropFilter: "blur(18px)",
  WebkitBackdropFilter: "blur(18px)",
  border: "1px solid rgba(96,165,250,0.1)",
  borderRadius: 14,
  padding: "18px 20px",
  boxShadow: "0 8px 32px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.04)",
};

const mainGrid = { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 28 };

const sectionLabel = {
  fontSize: 11, fontWeight: 700, color: "rgba(255,255,255,0.3)",
  textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 16,
};

const emptyMsg = { color: "#334155", fontSize: 13 };

const devRow    = { padding: "10px 6px", borderBottom: "1px solid rgba(255,255,255,0.04)", transition: "background 0.12s" };
const motivoRow = { padding: "12px 0",  borderBottom: "1px solid rgba(255,255,255,0.04)" };

const selectStyle = {
  background: "rgba(11,22,42,0.8)", border: "1px solid rgba(96,165,250,0.15)",
  borderRadius: 6, color: "#94a3b8", fontSize: 12, padding: "5px 10px",
  cursor: "pointer", outline: "none",
};

const tableWrap = { overflowX: "auto", borderRadius: 10, border: "1px solid rgba(96,165,250,0.07)" };
const table     = { width: "100%", borderCollapse: "collapse", fontSize: 13 };

const th = {
  textAlign: "left", padding: "10px 14px", fontSize: 11, fontWeight: 600,
  color: "#475569", background: "rgba(11,22,42,0.6)",
  borderBottom: "1px solid rgba(96,165,250,0.08)",
  whiteSpace: "nowrap", letterSpacing: "0.05em", textTransform: "uppercase",
  userSelect: "none",
};

const td    = { padding: "10px 14px", borderBottom: "1px solid rgba(255,255,255,0.04)", verticalAlign: "middle" };
const trRow = { transition: "background 0.12s" };

/* Modal form */
const formLabel = {
  fontSize: 11, color: "#475569", fontWeight: 600,
  textTransform: "uppercase", letterSpacing: "0.06em",
  display: "block", marginBottom: 6,
};

const formInput = {
  width: "100%", boxSizing: "border-box",
  padding: "9px 12px",
  background: "rgba(255,255,255,0.05)",
  border: "1px solid rgba(96,165,250,0.15)",
  borderRadius: 8,
  color: "#e2e8f0", fontSize: 13, outline: "none",
};

const btnSecondary = {
  padding: "8px 18px", borderRadius: 8,
  border: "1px solid rgba(255,255,255,0.1)",
  background: "transparent", color: "#94a3b8",
  fontSize: 13, cursor: "pointer",
};

const btnPrimary = {
  padding: "8px 22px", borderRadius: 8, border: "none",
  background: "linear-gradient(135deg, #534AB7, #3b82f6)",
  color: "#fff", fontSize: 13, fontWeight: 600,
};
